import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Building2, Mail, Phone, MapPin, Globe, Bell, User, ArrowLeft } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import { useAuth } from '../../context/AuthContext';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';

const Settings = () => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('personal');

    // College Data
    const [collegeData, setCollegeData] = useState({
        collegeName: '',
        collegeCode: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        accreditation: ''
    });

    // Personal Profile Data
    const [profileData, setProfileData] = useState({
        fullName: '',
        email: '',
        mobile: '',
        role: ''
    });

    // Notification Preferences
    const [notificationPrefs, setNotificationPrefs] = useState({
        newStudentEnrollment: true,
        reportGeneration: true,
        systemMaintenance: true,
        performanceAlerts: true,
        emailNotifications: true,
        pushNotifications: false
    });

    useEffect(() => {
        fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id]);

    const fetchAllData = async () => {
        setFetchLoading(true);
        try {
            await Promise.all([
                fetchCollegeData(),
                fetchProfileData()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setFetchLoading(false);
        }
    };

    const fetchCollegeData = async () => {
        try {
            const collegeId = user.college?._id || user.college;
            if (collegeId && collegeId !== 'null' && collegeId !== 'undefined') {
                const { data } = await api.get(`/colleges/${collegeId}`);
                if (data.success) {
                    const col = data.data;
                    setCollegeData({
                        collegeName: col.collegeName || '',
                        collegeCode: col.collegeCode || '',
                        email: col.email || '',
                        phone: col.contactNumber || '',
                        website: col.website || '',
                        address: col.address?.street || '',
                        city: col.address?.city || '',
                        state: col.address?.state || '',
                        pincode: col.address?.pincode || '',
                        accreditation: col.accreditation || col.accreditationStatus || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching college data:', error);
            toast.error('Failed to load college data');
        }
    };

    const fetchProfileData = async () => {
        try {
            if (user?._id) {
                const { data } = await api.get(`/users/${user._id}`);
                if (data.success) {
                    const profile = data.data;
                    setProfileData({
                        fullName: profile.fullName || '',
                        email: profile.email || '',
                        mobile: profile.mobile || '',
                        role: profile.role ? (profile.role.charAt(0).toUpperCase() + profile.role.slice(1)) : 'Institute Administrator'
                    });
                    
                    if (profile.notificationPreferences) {
                        setNotificationPrefs(prev => ({
                            ...prev,
                            ...profile.notificationPreferences
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching profile data:', error);
        }
    };

    const handleCollegeChange = (e) => {
        setCollegeData({ ...collegeData, [e.target.name]: e.target.value });
    };

    const handleProfileChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleNotificationChange = (key) => {
        setNotificationPrefs({ ...notificationPrefs, [key]: !notificationPrefs[key] });
    };

    const handleCollegeSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const collegeId = user.college?._id || user.college;
            if (!collegeId || collegeId === 'null' || collegeId === 'undefined') {
                toast.error('No college associated with your account');
                setLoading(false);
                return;
            }
            const payload = {
                collegeName: collegeData.collegeName,
                email: collegeData.email,
                contactNumber: collegeData.phone,
                website: collegeData.website,
                address: {
                    street: collegeData.address,
                    city: collegeData.city,
                    state: collegeData.state,
                    pincode: collegeData.pincode,
                    country: 'India'
                },
                accreditation: collegeData.accreditation
            };

            const { data } = await api.put(`/colleges/${collegeId}`, payload);
            if (data.success) {
                toast.success('Institute information updated successfully!');
                await fetchCollegeData();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update institute information');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.put(`/users/${user._id}`, {
                fullName: profileData.fullName,
                mobile: profileData.mobile
            });
            if (data.success) {
                toast.success('Profile updated successfully!');
                updateUser(data.data); // Update context
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.put(`/users/${user._id}/preferences`, {
                notificationPreferences: notificationPrefs
            });

            if (data.success) {
                toast.success('Notification preferences updated!');
            }
        } catch (error) {
            toast.error('Failed to update notification preferences');
        } finally {
            setLoading(false);
        }
    };

    const uploadProfileImage = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append('profileImage', file);

        setLoading(true);
        try {
            const { data } = await api.put('/auth/updatedetails', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (data.success) {
                toast.success('Profile picture updated successfully!');
                updateUser(data.data);
                fetchProfileData();
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
            toast.error('Failed to upload profile picture');
        } finally {
            setLoading(false);
        }
    };

    if (fetchLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-[#1a3884]/20 border-t-[#1a3884] rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium animate-pulse">Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Header Section matching Dashboard */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-xl px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147] animate-fade-in">
                <div className="flex items-center gap-4 text-left">
                    <button 
                        onClick={() => navigate('/dashboard')} 
                        className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors"
                        title="Back"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-[#1a3884] dark:text-white mb-1">
                            Settings
                        </h1>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">
                                Manage your account information and notification preferences
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                {/* Left Navigation Card */}
                <div className="lg:col-span-1 bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-4 transition-all duration-300">
                    <nav className="space-y-1">
                        {[
                            { id: 'personal', label: 'Personal Info', icon: User },
                            { id: 'notifications', label: 'Notifications', icon: Bell },
                        ].map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id)}
                                className={`w-full flex items-center px-4 py-3 text-sm font-bold rounded-lg transition-colors ${
                                    activeTab === item.id
                                        ? 'bg-[#002A5C]/10 dark:bg-[#1a3884]/20 text-[#002A5C] dark:text-[#4f7dff]'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a3884]/10 hover:text-[#002A5C] dark:hover:text-white'
                                }`}
                            >
                                <item.icon className={`mr-3 h-5 w-5 ${
                                    activeTab === item.id ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-400'
                                }`} />
                                {item.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Content Area */}
                <div className="lg:col-span-3">
                    {/* Personal Info Tab */}
                    {activeTab === 'personal' && (
                        <div className="space-y-6">
                            {/* Personal Info Card */}
                            <form onSubmit={handleProfileSubmit} className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                        <User className="text-white" size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Personal Information</h3>
                                        <p className="text-xs text-gray-500">Update your account name and mobile details.</p>
                                    </div>
                                </div>

                                {/* Profile Image Section */}
                                <div className="flex flex-col items-center justify-center border-b border-gray-100 dark:border-[#1a3884]/20 pb-6 mb-6">
                                    <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#001f40] dark:to-[#002147] rounded-full p-1 shadow-md border border-gray-100 dark:border-[#1a3884]/20 relative overflow-hidden flex items-center justify-center mb-3">
                                        {user?.profileImage ? (
                                            <img
                                                src={getFileUrl(user.profileImage)}
                                                alt="Profile"
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        ) : (
                                            <span className="bg-gradient-to-br from-[#1a3884] to-[#284BB3] text-transparent bg-clip-text text-4xl font-black">
                                                {(profileData.fullName || user?.fullName || 'U').charAt(0)}
                                            </span>
                                        )}
                                    </div>
                                    <label className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 dark:bg-[#001f40] dark:hover:bg-[#1a3884]/20 border border-gray-200 dark:border-[#1a3884]/45 rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer transition-colors">
                                        Change Photo
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => uploadProfileImage(e.target.files[0])}
                                        />
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Input
                                        label="Full Name"
                                        name="fullName"
                                        value={profileData.fullName}
                                        onChange={handleProfileChange}
                                        icon={User}
                                        required
                                    />

                                    <Input
                                        label="Email Address"
                                        name="email"
                                        type="email"
                                        value={profileData.email}
                                        disabled
                                        icon={Mail}
                                    />

                                    <Input
                                        label="Mobile Number"
                                        name="mobile"
                                        value={profileData.mobile}
                                        onChange={handleProfileChange}
                                        icon={Phone}
                                        required
                                    />

                                    <Input
                                        label="Role"
                                        name="role"
                                        value={profileData.role}
                                        disabled
                                    />

                                    <Input
                                        label="Organization"
                                        name="organization"
                                        value={
                                            user?.role === 'admin'
                                                ? 'SMAART Healthcare'
                                                : user?.college?.collegeName || collegeData.collegeName || 'N/A'
                                        }
                                        disabled
                                        icon={Building2}
                                    />
                                </div>

                                <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-[#1a3884]/20 mt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-5 py-2.5 rounded-lg text-sm font-bold bg-[#1a3884] hover:bg-[#284BB3] text-white shadow-md transition-all active:scale-95 cursor-pointer border-0 disabled:opacity-50"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>

                            {/* Additional forms for College Admin */}
                            {user?.role === 'college_admin' && (
                                <>
                                    {/* Institute Profile Card */}
                                    <form onSubmit={handleCollegeSubmit} className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                                <Building2 className="text-white" size={18} />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Institute Information</h3>
                                                <p className="text-xs text-gray-500">Update your institution's profile details.</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <Input
                                                label="Institute Name"
                                                name="collegeName"
                                                value={collegeData.collegeName}
                                                onChange={handleCollegeChange}
                                                icon={Building2}
                                                required
                                            />

                                            <Input
                                                label="Institute Code"
                                                name="collegeCode"
                                                value={collegeData.collegeCode}
                                                onChange={handleCollegeChange}
                                                disabled
                                            />

                                            <Input
                                                label="Accreditation"
                                                name="accreditation"
                                                value={collegeData.accreditation}
                                                onChange={handleCollegeChange}
                                                placeholder="e.g., NAAC A+"
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                name="description"
                                                rows={4}
                                                value={collegeData.description}
                                                onChange={handleCollegeChange}
                                                className="w-full rounded-lg border border-gray-300 dark:border-[#1a3884]/40 dark:bg-[#001f40] dark:text-white shadow-sm focus:border-[#1a3884] focus:ring-[#1a3884] p-3 text-sm outline-none"
                                                placeholder="Brief description about your institution..."
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <Input
                                                label="Website"
                                                name="website"
                                                value={collegeData.website}
                                                onChange={handleCollegeChange}
                                                icon={Globe}
                                                placeholder="https://www.example.com"
                                            />
                                        </div>

                                        <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-[#1a3884]/20 mt-6">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="px-5 py-2.5 rounded-lg text-sm font-bold bg-[#1a3884] hover:bg-[#284BB3] text-white shadow-md transition-all active:scale-95 cursor-pointer border-0 disabled:opacity-50"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>

                                    {/* Contact Details Card */}
                                    <form onSubmit={handleCollegeSubmit} className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                                <Phone className="text-white" size={18} />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Contact & Address Information</h3>
                                                <p className="text-xs text-gray-500">Manage contact information and street address details.</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <Input
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                value={collegeData.email}
                                                onChange={handleCollegeChange}
                                                icon={Mail}
                                                required
                                            />

                                            <Input
                                                label="Phone Number"
                                                name="phone"
                                                value={collegeData.phone}
                                                onChange={handleCollegeChange}
                                                icon={Phone}
                                                required
                                            />
                                        </div>

                                        <div className="border-t border-gray-100 dark:border-[#1a3884]/20 pt-6 mt-6">
                                            <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-4">Address Details</h4>
                                            <div className="grid grid-cols-1 gap-6">
                                                <Input
                                                    label="Street Address"
                                                    name="address"
                                                    value={collegeData.address}
                                                    onChange={handleCollegeChange}
                                                    icon={MapPin}
                                                    required
                                                />

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <Input
                                                        label="City"
                                                        name="city"
                                                        value={collegeData.city}
                                                        onChange={handleCollegeChange}
                                                        required
                                                    />
                                                    <Input
                                                        label="State"
                                                        name="state"
                                                        value={collegeData.state}
                                                        onChange={handleCollegeChange}
                                                        required
                                                        />
                                                    <Input
                                                        label="Pincode"
                                                        name="pincode"
                                                        value={collegeData.pincode}
                                                        onChange={handleCollegeChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-[#1a3884]/20 mt-6">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="px-5 py-2.5 rounded-lg text-sm font-bold bg-[#1a3884] hover:bg-[#284BB3] text-white shadow-md transition-all active:scale-95 cursor-pointer border-0 disabled:opacity-50"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <form onSubmit={handleNotificationSubmit} className="space-y-6 animate-fade-in">
                            {/* Email Notifications Card */}
                            <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                        <Mail className="text-white" size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Email Notifications</h3>
                                        <p className="text-xs text-gray-500">Configure what notifications you receive via email.</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        { key: 'emailNotifications', label: 'Email Notifications Enabled', description: 'Master toggle for all email alerts' },
                                        { key: 'newStudentEnrollment', label: 'Student Registrations', description: 'Get notified when a new student enrolls' },
                                        { key: 'reportGeneration', label: 'Assessment Updates', description: 'When reports or assessments are generated' },
                                        { key: 'teacherRegistration', label: 'Recruiter Activity', description: 'Alerts for recruiter registrations' },
                                        { key: 'systemMaintenance', label: 'System Announcements', description: 'Important system announcements and maintenance updates' },
                                    ].map((item) => (
                                        <div key={item.key} className="flex items-start justify-between py-3 border-b border-gray-100 dark:border-[#1a3884]/15 last:border-0">
                                            <div className="flex-1">
                                                <label htmlFor={item.key} className="font-bold text-gray-800 dark:text-white text-sm cursor-pointer">
                                                    {item.label}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleNotificationChange(item.key)}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f7dff] ${
                                                    notificationPrefs[item.key] ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-slate-600'
                                                }`}
                                            >
                                                <span
                                                    className={`${notificationPrefs[item.key] ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* In-App Notifications Card */}
                            <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                        <Bell className="text-white" size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">In-App Notifications</h3>
                                        <p className="text-xs text-gray-500">Configure alerts shown inside the web application dashboard.</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        { key: 'pushNotifications', label: 'In-App Notifications Enabled', description: 'Enable real-time push alerts' },
                                        { key: 'performanceAlerts', label: 'Performance Alerts', description: 'Receive alerts on system and student performance' }
                                    ].map((item) => (
                                        <div key={item.key} className="flex items-start justify-between py-3 border-b border-gray-100 dark:border-[#1a3884]/15 last:border-0">
                                            <div className="flex-1">
                                                <label htmlFor={item.key} className="font-bold text-gray-800 dark:text-white text-sm cursor-pointer">
                                                    {item.label}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleNotificationChange(item.key)}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f7dff] ${
                                                    notificationPrefs[item.key] ? 'bg-indigo-600 dark:bg-[#1a3884]' : 'bg-gray-200 dark:bg-slate-600'
                                                }`}
                                            >
                                                <span
                                                    className={`${notificationPrefs[item.key] ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-[#1a3884]/20 mt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-5 py-2.5 rounded-lg text-sm font-bold bg-[#1a3884] hover:bg-[#284BB3] text-white shadow-md transition-all active:scale-95 cursor-pointer border-0 disabled:opacity-50"
                                    >
                                        Save Preferences
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
